/* Product carousel layered on top of the welding hero.
   Native horizontal scrolling (swipe / trackpad) plus arrow buttons and an
   optional autoplay that pauses on interaction and when off-screen. */
(function () {
  'use strict';

  if (customElements.get('welding-hero-carousel')) return;

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class HeroCarousel extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-carousel-viewport]');
      this.track = this.querySelector('[data-carousel-track]');
      this.prevButton = this.querySelector('[data-carousel-prev]');
      this.nextButton = this.querySelector('[data-carousel-next]');

      if (!this.viewport || !this.track) return;

      this.interval = Math.max(parseInt(this.dataset.interval, 10) || 0, 1500);
      this.autoplayEnabled = this.dataset.autoplay === 'true' && !reducedMotion.matches;
      this.timer = null;
      this.paused = false;

      this.onScroll = this.throttle(this.updateButtons.bind(this), 100);
      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });

      if (this.prevButton) {
        this.prevButton.addEventListener('click', this.scrollByStep.bind(this, -1));
      }
      if (this.nextButton) {
        this.nextButton.addEventListener('click', this.scrollByStep.bind(this, 1));
      }

      this.updateButtons();
      this.setupAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this.viewport) this.viewport.removeEventListener('scroll', this.onScroll);
      if (this.observer) this.observer.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    /* --------------------------------------------------------------
       Scrolling
    -------------------------------------------------------------- */

    get step() {
      var slide = this.track.firstElementChild;
      if (!slide) return this.viewport.clientWidth;

      var styles = window.getComputedStyle(this.track);
      var gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return slide.getBoundingClientRect().width + gap;
    }

    get maxScroll() {
      return this.viewport.scrollWidth - this.viewport.clientWidth;
    }

    scrollByStep(direction, event) {
      if (event) event.preventDefault();
      this.scrollTo(this.viewport.scrollLeft + this.step * direction);
    }

    scrollTo(position) {
      var behavior = reducedMotion.matches ? 'auto' : 'smooth';
      this.viewport.scrollTo({ left: Math.max(0, Math.min(position, this.maxScroll)), behavior: behavior });
    }

    advance() {
      // Loop back to the start once the last card is fully visible.
      if (this.viewport.scrollLeft >= this.maxScroll - 2) {
        this.scrollTo(0);
      } else {
        this.scrollByStep(1);
      }
    }

    updateButtons() {
      if (this.prevButton) {
        this.prevButton.disabled = this.viewport.scrollLeft <= 2;
      }
      if (this.nextButton) {
        this.nextButton.disabled = this.viewport.scrollLeft >= this.maxScroll - 2;
      }
    }

    /* --------------------------------------------------------------
       Autoplay
    -------------------------------------------------------------- */

    setupAutoplay() {
      if (!this.autoplayEnabled) return;

      ['mouseenter', 'focusin', 'pointerdown', 'touchstart'].forEach((eventName) => {
        this.addEventListener(eventName, this.pause.bind(this), { passive: true });
      });
      ['mouseleave', 'focusout', 'pointerup', 'pointercancel', 'touchend'].forEach((eventName) => {
        this.addEventListener(eventName, this.resume.bind(this), { passive: true });
      });

      this.onVisibilityChange = () => {
        if (document.hidden) {
          this.stopAutoplay();
        } else if (!this.paused) {
          this.startAutoplay();
        }
      };
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.paused) {
              this.startAutoplay();
            } else {
              this.stopAutoplay();
            }
          });
        });
        this.observer.observe(this);
      } else {
        this.startAutoplay();
      }
    }

    startAutoplay() {
      if (this.timer || !this.autoplayEnabled) return;
      this.timer = setInterval(this.advance.bind(this), this.interval);
    }

    stopAutoplay() {
      if (!this.timer) return;
      clearInterval(this.timer);
      this.timer = null;
    }

    pause() {
      this.paused = true;
      this.stopAutoplay();
    }

    resume() {
      this.paused = false;
      this.startAutoplay();
    }

    /* --------------------------------------------------------------
       Helpers
    -------------------------------------------------------------- */

    throttle(callback, wait) {
      var waiting = false;
      return function () {
        if (waiting) return;
        waiting = true;
        window.setTimeout(function () {
          waiting = false;
          callback();
        }, wait);
      };
    }
  }

  customElements.define('welding-hero-carousel', HeroCarousel);
})();
